// Storage Service - Firebase Storage for Image Uploads

import { ref, uploadBytes, getDownloadURL, deleteObject } from '@firebase/storage';
import { storage } from './firebase';

// Image upload configuration
const MAX_FILE_SIZE_BEFORE_COMPRESSION = 50 * 1024 * 1024; // 50MB - we'll compress it down
const MAX_FILE_SIZE_AFTER_COMPRESSION = 4 * 1024 * 1024; // 4MB target (Firebase rule is < 5MB)
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];

export interface UploadResult {
  url: string;
  path: string;
}

export interface UploadProgress {
  progress: number; // 0-100
  status: 'uploading' | 'complete' | 'error';
}

/**
 * Validates an image file before upload (before compression)
 */
export function validateImageFile(file: File): string | null {
  if (!file) {
    return 'Keine Datei ausgewaehlt';
  }

  // Check file type - allow common image types including HEIC from iPhones
  const isImage = file.type.startsWith('image/') || ALLOWED_TYPES.includes(file.type);
  if (!isImage) {
    return 'Nur Bilddateien erlaubt (JPG, PNG, WebP, GIF, HEIC)';
  }

  // Allow larger files since we'll compress them
  if (file.size > MAX_FILE_SIZE_BEFORE_COMPRESSION) {
    return 'Datei zu gross (max. 50MB)';
  }

  return null; // No error
}

/**
 * Generates a unique filename for an image
 */
function generateFilename(originalName: string): string {
  const extension = originalName.split('.').pop()?.toLowerCase() || 'jpg';
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${randomStr}.${extension}`;
}

/**
 * Uploads a single image to Firebase Storage
 * Automatically compresses the image before upload
 */
export async function uploadPlaceImage(
  file: File,
  placeId?: string
): Promise<UploadResult> {
  // Validate file
  const validationError = validateImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  // Compress image before upload
  console.log(`[uploadPlaceImage] Original size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
  const compressedFile = await compressImage(file, 1920, 0.85);
  console.log(`[uploadPlaceImage] Compressed size: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);

  // Generate path
  const filename = generateFilename(compressedFile.name);
  const folder = placeId ? `places/${placeId}` : 'places/temp';
  const path = `${folder}/${filename}`;

  // Create reference and upload
  const storageRef = ref(storage, path);

  try {
    await uploadBytes(storageRef, compressedFile, {
      contentType: compressedFile.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
      },
    });

    // Get download URL
    const url = await getDownloadURL(storageRef);

    return { url, path };
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Fehler beim Hochladen des Bildes');
  }
}

/**
 * Uploads multiple images in parallel for better performance
 */
export async function uploadPlaceImages(
  files: File[],
  placeId?: string
): Promise<UploadResult[]> {
  // Upload all files in parallel using Promise.all
  const uploadPromises = files.map(file => uploadPlaceImage(file, placeId));
  return Promise.all(uploadPromises);
}

/**
 * Deletes an image from Firebase Storage
 */
export async function deletePlaceImage(path: string): Promise<void> {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw - image may not exist
  }
}

/**
 * Compresses an image before upload (client-side)
 * Automatically adjusts quality to meet target file size
 */
export async function compressImage(
  file: File,
  maxWidth: number = 1920,
  initialQuality: number = 0.85,
  targetSize: number = MAX_FILE_SIZE_AFTER_COMPRESSION
): Promise<File> {
  // If file is already small enough and a web-friendly format, return as-is
  if (file.size <= targetSize && (file.type === 'image/jpeg' || file.type === 'image/webp')) {
    console.log(`[compressImage] File already small enough: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    return file;
  }

  return new Promise((resolve, reject) => {
    // Timeout to prevent hanging
    const timeout = setTimeout(() => {
      console.error('[compressImage] Timeout - returning original file');
      resolve(file);
    }, 30000);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = async () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          console.log(`[compressImage] Original dimensions: ${width}x${height}`);

          // Scale down if needed
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          // Also limit height to prevent extremely tall images
          const maxHeight = maxWidth * 1.5;
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = Math.round(width);
          canvas.height = Math.round(height);

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.error('[compressImage] Canvas context failed');
            clearTimeout(timeout);
            resolve(file); // Return original if canvas fails
            return;
          }

          // Use better image smoothing for downscaling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Try to compress to target size, reducing quality iteratively if needed
          let quality = initialQuality;
          let blob: Blob | null = null;
          let attempts = 0;
          const maxAttempts = 5;

          while (attempts < maxAttempts) {
            blob = await new Promise<Blob | null>((res) => {
              canvas.toBlob((b) => res(b), 'image/jpeg', quality);
            });

            if (!blob) {
              console.error('[compressImage] toBlob returned null');
              break;
            }

            // If size is acceptable or quality is already low, stop
            if (blob.size <= targetSize || quality <= 0.4) {
              break;
            }

            // Reduce quality for next attempt
            quality -= 0.15;
            attempts++;
          }

          clearTimeout(timeout);

          if (!blob) {
            console.error('[compressImage] No blob created, returning original');
            resolve(file);
            return;
          }

          const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          console.log(`[compressImage] Compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (${canvas.width}x${canvas.height}, q=${quality.toFixed(2)})`);
          resolve(compressedFile);
        } catch (err) {
          console.error('[compressImage] Error during compression:', err);
          clearTimeout(timeout);
          resolve(file); // Return original on error
        }
      };
      img.onerror = (err) => {
        console.error('[compressImage] Image load error:', err);
        clearTimeout(timeout);
        // Return original file instead of rejecting - allows upload of unsupported formats
        resolve(file);
      };
    };
    reader.onerror = (err) => {
      console.error('[compressImage] FileReader error:', err);
      clearTimeout(timeout);
      resolve(file); // Return original on error
    };
  });
}

// ============================================
// GROUP IMAGES
// ============================================

/**
 * Uploads an image for a group (cover or avatar)
 */
export async function uploadGroupImage(
  file: File,
  groupId: string,
  type: 'cover' | 'avatar' = 'cover'
): Promise<UploadResult> {
  console.log('[uploadGroupImage] Starting upload:', { groupId, type, fileName: file.name });

  // Validate file
  const validationError = validateImageFile(file);
  if (validationError) {
    console.error('[uploadGroupImage] Validation error:', validationError);
    throw new Error(validationError);
  }

  // Compress image before upload
  const maxWidth = type === 'cover' ? 1920 : 400;
  console.log('[uploadGroupImage] Compressing image...');
  const compressedFile = await compressImage(file, maxWidth, 0.85);
  console.log('[uploadGroupImage] Compressed:', { size: compressedFile.size, type: compressedFile.type });

  // Generate path
  const filename = generateFilename(file.name);
  const path = `groups/${groupId}/${type}/${filename}`;
  console.log('[uploadGroupImage] Upload path:', path);

  // Create reference and upload
  const storageRef = ref(storage, path);

  try {
    console.log('[uploadGroupImage] Uploading to Firebase Storage...');
    await uploadBytes(storageRef, compressedFile, {
      contentType: compressedFile.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        type: type,
      },
    });
    console.log('[uploadGroupImage] Upload complete, getting download URL...');

    // Get download URL
    const url = await getDownloadURL(storageRef);
    console.log('[uploadGroupImage] Success, URL:', url);

    return { url, path };
  } catch (error: any) {
    console.error('[uploadGroupImage] Error:', error);
    console.error('[uploadGroupImage] Error code:', error.code);
    console.error('[uploadGroupImage] Error message:', error.message);
    throw new Error(`Fehler beim Hochladen: ${error.code || error.message}`);
  }
}

// ============================================
// NOTE PHOTOS (Logbuch)
// ============================================

/**
 * Uploads an image for a user's note (private photos)
 */
export async function uploadNotePhoto(
  file: File,
  userId: string,
  noteId?: string
): Promise<UploadResult> {
  // Validate file
  const validationError = validateImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  // Compress image before upload
  const compressedFile = await compressImage(file, 1200, 0.8);

  // Generate path: userNotes/{userId}/{noteId or temp}/{filename}
  const filename = generateFilename(file.name);
  const folder = noteId ? `userNotes/${userId}/${noteId}` : `userNotes/${userId}/temp`;
  const path = `${folder}/${filename}`;

  // Create reference and upload
  const storageRef = ref(storage, path);

  try {
    await uploadBytes(storageRef, compressedFile, {
      contentType: compressedFile.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        userId: userId,
      },
    });

    // Get download URL
    const url = await getDownloadURL(storageRef);

    return { url, path };
  } catch (error) {
    console.error('Error uploading note photo:', error);
    throw new Error('Fehler beim Hochladen des Fotos');
  }
}

/**
 * Generates a thumbnail for faster loading
 */
export async function generateThumbnail(
  file: File,
  maxSize: number = 200
): Promise<File> {
  return compressImage(file, maxSize, 0.6);
}

/**
 * Uploads a note photo with thumbnail
 */
export async function uploadNotePhotoWithThumbnail(
  file: File,
  userId: string,
  noteId?: string
): Promise<{ url: string; thumbnailUrl: string; path: string }> {
  // Upload main image
  const mainResult = await uploadNotePhoto(file, userId, noteId);

  // Generate and upload thumbnail
  const thumbnailFile = await generateThumbnail(file, 300);
  const thumbnailFilename = `thumb_${generateFilename(file.name)}`;
  const folder = noteId ? `userNotes/${userId}/${noteId}` : `userNotes/${userId}/temp`;
  const thumbnailPath = `${folder}/${thumbnailFilename}`;

  const thumbnailRef = ref(storage, thumbnailPath);

  try {
    await uploadBytes(thumbnailRef, thumbnailFile, {
      contentType: thumbnailFile.type,
    });
    const thumbnailUrl = await getDownloadURL(thumbnailRef);

    return {
      url: mainResult.url,
      thumbnailUrl,
      path: mainResult.path,
    };
  } catch {
    // If thumbnail fails, return main image for both
    return {
      url: mainResult.url,
      thumbnailUrl: mainResult.url,
      path: mainResult.path,
    };
  }
}

/**
 * Deletes all photos for a note
 */
export async function deleteNotePhotos(
  userId: string,
  noteId: string,
  photoPaths?: string[]
): Promise<void> {
  if (!photoPaths || photoPaths.length === 0) return;

  const deletePromises = photoPaths.map(async (path) => {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting note photo:', error);
      // Don't throw - continue with other deletions
    }
  });

  await Promise.all(deletePromises);
}
